import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { cn } from '../../lib/utils';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import HelpDrawer from '../admin/HelpDrawer';

const NavDropdown = ({ label, icon, items, isActive }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium transition-colors duration-200 rounded-t-lg gap-1.5",
          isActive
            ? "border-primary text-gray-900 bg-blue-50"
            : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 hover:bg-gray-50"
        )}
      >
        {icon && <SafeIcon icon={icon} className="text-base" />}
        {label}
        <SafeIcon icon={FiIcons.FiChevronDown} className={cn("text-xs transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-50">
          {items.map((item) => (
            <div key={item.key || item.path}>
              {item.path ? (
                <Link
                  to={item.path}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  {item.icon && <SafeIcon icon={item.icon} className="text-gray-400 text-base" />}
                  {item.label}
                </Link>
              ) : (
                <button
                  onClick={() => { item.onClick?.(); setOpen(false); }}
                  className={cn(
                    "flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors w-full text-left",
                    item.danger
                      ? "text-red-600 hover:bg-red-50"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  {item.icon && <SafeIcon icon={item.icon} className="text-base" />}
                  {item.label}
                </button>
              )}
              {item.dividerAfter && <div className="border-t border-gray-100 my-1" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useStore();
  const { isAdmin, logout } = useAuth();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);

  const isEmbedRoute = location.pathname === '/widget' || location.pathname === '/calendar' || location.pathname === '/booking';
  if (isEmbedRoute) return null;

  const userLinks = [
    { path: '/', label: 'Home' },
    { path: '/booking', label: 'Book Event' },
  ];

  const primaryAdminLinks = [
    { path: '/admin/dashboard', icon: FiIcons.FiGrid, label: 'Dashboard' },
    { path: '/admin/bookings', icon: FiIcons.FiInbox, label: 'Requests' },
    { path: '/admin/calendar', icon: FiIcons.FiCalendar, label: 'Calendar' },
  ];

  const setupLinks = [
    { path: '/admin/event-types', icon: FiIcons.FiTag, label: 'Event Types' },
    { path: '/admin/pricing-rules', icon: FiIcons.FiDollarSign, label: 'Pricing' },
    { path: '/admin/addons', icon: FiIcons.FiPackage, label: 'Add-ons' },
  ];

  const setupPaths = setupLinks.map(l => l.path);
  const isSetupActive = setupPaths.includes(location.pathname);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const systemLinks = [
    { path: '/admin/settings', icon: FiIcons.FiSettings, label: 'Settings' },
    { path: '/admin/users', icon: FiIcons.FiUsers, label: 'Team', dividerAfter: true },
    { key: 'help', icon: FiIcons.FiHelpCircle, label: 'Help Guide', onClick: () => setHelpDrawerOpen(true), dividerAfter: true },
    { key: 'exit', icon: FiIcons.FiArrowLeft, label: 'Exit Admin', onClick: () => navigate('/') },
    { key: 'logout', icon: FiIcons.FiLogOut, label: 'Logout', onClick: handleLogout, danger: true },
  ];

  const systemPaths = ['/admin/settings', '/admin/users'];
  const isSystemActive = systemPaths.includes(location.pathname);

  const links = isAdminRoute ? primaryAdminLinks : userLinks;

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 hidden md:block shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0 flex items-center gap-3">
                {settings.logo ? (
                  <img
                    src={settings.logo}
                    alt={settings.companyName || 'Logo'}
                    className="h-10 w-auto object-contain"
                  />
                ) : (
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-white">
                    <SafeIcon icon={FiIcons.FiCalendar} className="text-lg" />
                  </div>
                )}
                <span className="font-bold text-xl tracking-tight text-gray-900">
                  {settings.companyName || 'Luxe Events'}
                </span>
              </Link>

              <div className="hidden md:ml-10 md:flex md:items-center md:space-x-1">
                {links.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={cn(
                      "inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium transition-colors duration-200 rounded-t-lg",
                      location.pathname === link.path
                        ? "border-primary text-gray-900 bg-blue-50"
                        : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    {link.icon && <SafeIcon icon={link.icon} className="mr-2 text-base" />}
                    {link.label}
                  </Link>
                ))}
                {isAdminRoute && (
                  <NavDropdown
                    label="Setup"
                    icon={FiIcons.FiSliders}
                    items={setupLinks}
                    isActive={isSetupActive}
                  />
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!isAdminRoute && !isAdmin && (
                <Link to="/admin/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-50">
                  Admin Login
                </Link>
              )}
              {!isAdminRoute && isAdmin && (
                <Link to="/admin/dashboard" className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-50">
                  Admin Panel
                </Link>
              )}
              {isAdminRoute && (
                <NavDropdown
                  icon={FiIcons.FiSettings}
                  label="System"
                  items={systemLinks}
                  isActive={isSystemActive}
                />
              )}
            </div>
          </div>
        </div>
      </nav>

      {isAdminRoute && (
        <HelpDrawer isOpen={helpDrawerOpen} onClose={() => setHelpDrawerOpen(false)} />
      )}
    </>
  );
};

export default Navbar;
